import { extractHandoutPaginationPlan, stripHandoutPaginationComment, embedHandoutPaginationComment } from './handoutPagination'
import { HANDOUT_QUESTION_INDEX_STYLE, cleanupStrayQuestionIndexNodes, isQuestionMetaText, stripLeadingQuestionIndex } from './handoutDecorate'

function isQuestionMetaElement(node) {
  if (!node || node.nodeType !== 1) return false
  if (node.closest && node.closest('table')) return false
  if (node.classList && node.classList.contains('handout-inline-question')) return true
  const text = (node.textContent || '').replace(/\s+/g, ' ').trim()
  return isQuestionMetaText(text)
}

function createQuestionBlockId(metaNode, index) {
  const existing = metaNode.getAttribute('data-handout-question-block')
  if (existing) return existing
  const questionId = metaNode.getAttribute('data-question-id') || ''
  if (questionId) return `q-${questionId}-${index + 1}`
  const text = (metaNode.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 48)
  const digest = text.split('').reduce((sum, ch) => sum + ch.charCodeAt(0), 0)
  return `block-${index + 1}-${digest}`
}

function markQuestionBlockNode(node, blockId, metaNode) {
  if (!node || !blockId) return
  node.setAttribute('data-handout-question-block', blockId)
  const questionId = metaNode.getAttribute('data-question-id')
  if (questionId && !node.getAttribute('data-question-id')) {
    node.setAttribute('data-question-id', questionId)
  }
  const title = metaNode.getAttribute('data-question-title')
  if (title && !node.getAttribute('data-question-title')) {
    node.setAttribute('data-question-title', title)
  }
}

export function findQuestionMetaNodes(root) {
  if (!root) return []
  const candidates = Array.from(root.querySelectorAll('p,div')).filter(isQuestionMetaElement)
  return candidates.filter(node => !candidates.some(other => other !== node && other.contains(node)))
}

function clearQuestionBlockIdentity(node) {
  if (!node || !node.removeAttribute) return
  node.removeAttribute('data-handout-question-block')
  node.removeAttribute('data-question-id')
  node.removeAttribute('data-question-title')
  node.removeAttribute('data-handout-option-layout')
  node.removeAttribute('data-handout-question-option')
  if (node.classList) node.classList.remove('handout-question-option')
}

export function isHandoutOptionElement(node) {
  if (!node || node.nodeType !== 1) return false
  if (node.classList && node.classList.contains('handout-question-options')) return false
  if (node.getAttribute && node.getAttribute('data-handout-question-option') === 'true') return true
  if (node.classList && node.classList.contains('handout-question-option')) return true
  const strong = node.querySelector && node.querySelector('strong, b')
  if (!strong) return false
  return /^[A-H][.、．]?$/i.test((strong.textContent || '').trim())
}

function clearTrailingQuestionBlockMarks(metaNode, blockId, coreNodes) {
  if (!coreNodes.length) return
  let current = coreNodes[coreNodes.length - 1].nextElementSibling
  while (current) {
    if (current.classList && current.classList.contains('handout-inline-question')) break
    if (isQuestionMetaElement(current)) break
    const nextBlockId = current.getAttribute && current.getAttribute('data-handout-question-block')
    if (nextBlockId && nextBlockId !== blockId) break
    clearQuestionBlockIdentity(current)
    current = current.nextElementSibling
  }
}

export function ensureQuestionBlockIds(root) {
  if (!root) return
  findQuestionMetaNodes(root).forEach((metaNode, index) => {
    if (!metaNode.classList.contains('handout-inline-question')) {
      metaNode.classList.add('handout-inline-question')
    }
    const blockId = metaNode.getAttribute('data-handout-question-block') || createQuestionBlockId(metaNode, index)
    const coreNodes = collectQuestionBlockNodes(metaNode, blockId)
    coreNodes.forEach(node => markQuestionBlockNode(node, blockId, metaNode))
    clearTrailingQuestionBlockMarks(metaNode, blockId, coreNodes)
  })
}

export function collectQuestionBlockNodes(startNode, blockId) {
  if (!startNode || !blockId) return []
  const nodes = [startNode]
  let current = startNode.nextElementSibling
  let optionStarted = false
  while (current) {
    if (current.classList && current.classList.contains('handout-inline-question')) break
    if (isQuestionMetaElement(current)) break
    const nextBlockId = current.getAttribute && current.getAttribute('data-handout-question-block')
    if (nextBlockId && nextBlockId !== blockId) break
    if (current.classList && current.classList.contains('handout-question-options')) {
      optionStarted = true
      nodes.push(current)
      current = current.nextElementSibling
      continue
    }
    if (isHandoutOptionElement(current)) {
      optionStarted = true
      nodes.push(current)
      current = current.nextElementSibling
      continue
    }
    if (!optionStarted) {
      if (isHandoutOptionElement(current)) break
      nodes.push(current)
      current = current.nextElementSibling
      continue
    }
    break
  }
  return nodes
}

export function isWithinQuestionCoreBlock(metaNode, blockId, target) {
  if (!metaNode || !blockId || !target) return false
  const coreNodes = collectQuestionBlockNodes(metaNode, blockId)
  return coreNodes.some(node => node === target || (node.contains && node.contains(target)))
}

/** 计算选中高亮框用的节点：题头 + 题干 + 本题全部选项（按 blockId 匹配，排除上一题残留） */
export function collectQuestionHighlightNodes(metaNode, blockId, root) {
  if (!metaNode || !blockId) return []
  const surface = root || metaNode.parentElement
  const nodes = [metaNode]
  let current = metaNode.nextElementSibling
  let optionStarted = false

  while (current) {
    if (current.classList && current.classList.contains('handout-inline-question')) break
    if (isQuestionMetaElement(current)) break
    const bid = current.getAttribute && current.getAttribute('data-handout-question-block')
    if (bid && bid !== blockId) break

    const isOption = isHandoutOptionElement(current) ||
      (current.classList && current.classList.contains('handout-question-options') && (!bid || bid === blockId))

    if (isOption) {
      optionStarted = true
      nodes.push(current)
    } else if (!optionStarted) {
      if (isHandoutOptionElement(current)) break
      nodes.push(current)
    } else {
      break
    }
    current = current.nextElementSibling
  }

  if (surface && surface.querySelectorAll) {
    surface.querySelectorAll(
      `[data-handout-question-block="${blockId}"][data-handout-question-option="true"], .handout-question-option[data-handout-question-block="${blockId}"]`
    ).forEach(node => {
      if (!nodes.includes(node)) nodes.push(node)
    })
  }

  nodes.sort((a, b) => {
    if (a === b) return 0
    const pos = a.compareDocumentPosition(b)
    if (pos & Node.DOCUMENT_POSITION_FOLLOWING) return -1
    if (pos & Node.DOCUMENT_POSITION_PRECEDING) return 1
    return 0
  })
  return nodes
}

export function getQuestionBlockRangesFromRoot(root) {
  if (!root) return []
  ensureQuestionBlockIds(root)
  return findQuestionMetaNodes(root).map((metaNode, index) => {
    const blockId = metaNode.getAttribute('data-handout-question-block') || createQuestionBlockId(metaNode, index)
    markQuestionBlockNode(metaNode, blockId, metaNode)
    const nodes = collectQuestionBlockNodes(metaNode, blockId)
    nodes.forEach(node => markQuestionBlockNode(node, blockId, metaNode))
    return {
      blockId,
      questionId: metaNode.getAttribute('data-question-id') || '',
      title: metaNode.getAttribute('data-question-title') || '',
      optionLayout: metaNode.getAttribute('data-handout-option-layout') || '1',
      metaNode,
      nodes
    }
  })
}

export function getQuestionBlockRangesFromHtml(html) {
  if (!html || typeof DOMParser === 'undefined') {
    return { root: null, ranges: [], plan: null }
  }
  const plan = extractHandoutPaginationPlan(html)
  const bodyHtml = stripHandoutPaginationComment(html)
  const doc = new DOMParser().parseFromString(`<div id="root">${bodyHtml}</div>`, 'text/html')
  const root = doc.getElementById('root')
  if (!root) {
    return { root: null, ranges: [], plan }
  }
  return { root, ranges: getQuestionBlockRangesFromRoot(root), plan }
}

function ensureQuestionIndexElement(node, doc) {
  const document = doc || node.ownerDocument
  const indexNodes = Array.from(node.querySelectorAll('[data-handout-question-index], .handout-question-index'))
  indexNodes.slice(1).forEach(item => item.remove())

  Array.from(node.childNodes).forEach(child => {
    if (child.nodeType !== 1 || child.tagName !== 'STRONG') return
    const compact = (child.textContent || '').replace(/\s+/g, '').trim()
    if (!/^\d+\.?$/.test(compact)) return
    if (node.querySelector('[data-handout-question-index], .handout-question-index')) {
      child.remove()
    }
  })

  let indexNode = node.querySelector('[data-handout-question-index], .handout-question-index')
  if (!indexNode) {
    const legacyStrong = Array.from(node.childNodes).find(child => (
      child.nodeType === 1
      && child.tagName === 'STRONG'
      && /^\d+\.?$/.test((child.textContent || '').replace(/\s+/g, '').trim())
    ))
    indexNode = document.createElement('span')
    indexNode.className = 'handout-question-index'
    indexNode.setAttribute('data-handout-question-index', 'true')
    indexNode.setAttribute('style', HANDOUT_QUESTION_INDEX_STYLE)
    if (legacyStrong) {
      legacyStrong.replaceWith(indexNode)
    } else {
      node.insertBefore(indexNode, node.firstChild)
    }
  } else if (indexNode.tagName === 'STRONG') {
    const span = document.createElement('span')
    span.className = 'handout-question-index'
    span.setAttribute('data-handout-question-index', 'true')
    span.setAttribute('style', HANDOUT_QUESTION_INDEX_STYLE)
    span.textContent = indexNode.textContent
    indexNode.replaceWith(span)
    indexNode = span
  } else if (!indexNode.getAttribute('style')) {
    indexNode.setAttribute('style', HANDOUT_QUESTION_INDEX_STYLE)
  }
  return indexNode
}

export function renumberInlineQuestionIndices(root) {
  if (!root) return
  let index = 1
  findQuestionMetaNodes(root).forEach(node => {
    cleanupStrayQuestionIndexNodes(node)
    const indexNode = ensureQuestionIndexElement(node, root.ownerDocument)
    indexNode.textContent = `${index}.`
    const typeSpan = node.querySelector('[data-handout-question-type], .handout-question-type')
    if (typeSpan) {
      const cleaned = stripLeadingQuestionIndex((typeSpan.textContent || '').trim())
      if (cleaned !== (typeSpan.textContent || '').trim()) {
        typeSpan.textContent = cleaned
      }
    }
    cleanupStrayQuestionIndexNodes(node)
    index += 1
  })
}

export function renumberQuestionsInHtml(html) {
  const { root, plan } = getQuestionBlockRangesFromHtml(html)
  if (!root) return html
  renumberInlineQuestionIndices(root)
  return serializeQuestionBlockRoot(root, plan)
}

export function serializeQuestionBlockRoot(root, plan) {
  if (!root) return ''
  const body = root.innerHTML.trim()
  return plan ? embedHandoutPaginationComment(body, plan) : body
}

export function moveQuestionBlockByStepInRoot(root, blockId, step) {
  if (!root || !blockId || !step) return false
  const ranges = getQuestionBlockRangesFromRoot(root)
  if (!ranges.length) return false
  const index = ranges.findIndex(item => item.blockId === blockId)
  const targetIndex = index + step
  if (index < 0 || targetIndex < 0 || targetIndex >= ranges.length) return false
  return moveQuestionBlockToTargetInRoot(root, blockId, ranges[targetIndex].blockId, step < 0 ? 'before' : 'after')
}

export function moveQuestionBlockToTargetInRoot(root, sourceBlockId, targetBlockId, position) {
  if (!root || !sourceBlockId || !targetBlockId || sourceBlockId === targetBlockId) return false
  const ranges = getQuestionBlockRangesFromRoot(root)
  const source = ranges.find(item => item.blockId === sourceBlockId)
  const target = ranges.find(item => item.blockId === targetBlockId)
  if (!source || !target || !source.nodes.length || !target.nodes.length) return false
  const parent = source.nodes[0].parentNode
  if (!parent) return false
  const fragment = root.ownerDocument.createDocumentFragment()
  source.nodes.forEach(node => fragment.appendChild(node))
  if (position === 'before') {
    parent.insertBefore(fragment, target.nodes[0])
  } else {
    parent.insertBefore(fragment, target.nodes[target.nodes.length - 1].nextSibling)
  }
  renumberInlineQuestionIndices(root)
  return true
}

export function moveQuestionBlockInHtml(html, blockId, step) {
  const { root, ranges, plan } = getQuestionBlockRangesFromHtml(html)
  if (!root || !ranges.length) return html
  if (!moveQuestionBlockByStepInRoot(root, blockId, step)) return html
  return serializeQuestionBlockRoot(root, plan)
}

export function moveQuestionBlockToTargetInHtml(html, sourceBlockId, targetBlockId, position) {
  const { root, plan } = getQuestionBlockRangesFromHtml(html)
  if (!root) return html
  if (!moveQuestionBlockToTargetInRoot(root, sourceBlockId, targetBlockId, position)) return html
  return serializeQuestionBlockRoot(root, plan)
}

export function moveQuestionBlockByIndexInHtml(html, sourceIndex, targetIndex, position) {
  const { root, ranges, plan } = getQuestionBlockRangesFromHtml(html)
  if (!root || !ranges.length) return html
  if (sourceIndex < 0 || targetIndex < 0 || sourceIndex >= ranges.length || targetIndex >= ranges.length) return html
  if (sourceIndex === targetIndex) return html
  const sourceBlockId = ranges[sourceIndex].blockId
  const targetBlockId = ranges[targetIndex].blockId
  if (!moveQuestionBlockToTargetInRoot(root, sourceBlockId, targetBlockId, position)) return html
  return serializeQuestionBlockRoot(root, plan)
}

export function removeQuestionBlockFromRoot(root, blockId) {
  if (!root || !blockId) return false
  const ranges = getQuestionBlockRangesFromRoot(root)
  const range = ranges.find(item => item.blockId === blockId)
  if (!range) return false
  range.nodes.forEach(node => node.remove())
  renumberInlineQuestionIndices(root)
  return true
}

export function removeQuestionBlockFromHtml(html, blockId) {
  const { root, plan } = getQuestionBlockRangesFromHtml(html)
  if (!root || !removeQuestionBlockFromRoot(root, blockId)) return html
  return serializeQuestionBlockRoot(root, plan)
}

export function resolveQuestionBlockMetaFromTarget(root, target) {
  if (!root || !target || !target.closest) return null
  ensureQuestionBlockIds(root)
  const node = target.closest('.handout-inline-question, [data-handout-question-block]')
  if (!node) return null
  const blockId = node.getAttribute('data-handout-question-block')
  if (!blockId) return node.classList.contains('handout-inline-question') ? node : null
  const meta = root.querySelector(`.handout-inline-question[data-handout-question-block="${blockId}"]`)
  return meta || node
}

export function resolveQuestionDropPosition(metaNode, clientY) {
  if (!metaNode) return 'before'
  const rect = metaNode.getBoundingClientRect()
  if (!rect || !rect.height) return 'before'
  return clientY < rect.top + rect.height / 2 ? 'before' : 'after'
}
